<script lang="ts" setup>
import { computed, ref } from 'vue';
import {
  BAlert,
  BBadge,
  BButton,
  BButtonGroup,
  BCard,
  BFormCheckbox,
  BFormSelect,
  BModal,
  BSpinner,
  BTable,
  BFormTextarea,
} from 'bootstrap-vue-next';
import { useMutation } from '@vue/apollo-composable';
import {
  OrganizationOwnerUpdateDocument,
  OrganizationUserAssociationUpdateDocument,
  OrganizationUserAssociationRemoveDocument,
  OrganizationUserAssociationType,
} from '@/gql/graphql';
import { formatDate } from '@/utils/date-utils';
import { useUserStore } from '@/store/user';
import {
  ORGANIZATION_USER_ROLES,
  OrganizationMembershipModeSelectOptions,
} from '@/constants';
import OrganizationActivate from '@/components/organization/OrganizationActivate.vue';

// --- Props for the component ---
interface Props {
  associations: OrganizationUserAssociationType[];
  isLoading: boolean;
  isError: boolean;
  canManage?: boolean;
  isOwner?: boolean;
  showActiveStatus?: boolean;
  showEmail?: boolean;
  showOrganization?: boolean;
}
const props = defineProps<Props>();
const emits = defineEmits(['membershipsUpdated', 'rowClicked']);

const userStore = useUserStore();

// Refs to handle the modal & the ID of the association to remove
const showAssocActivateModal = ref(false);
const showAssocEditModal = ref(false);
const showUpdateOwnerModal = ref(false);
const showRemoveModal = ref(false);
const assocToActivate = ref<OrganizationUserAssociationType | null>(null);
const assocToEdit = ref<OrganizationUserAssociationType | null>(null);
const assocNewOwner = ref<OrganizationUserAssociationType | null>(null);
const assocToRemove = ref<OrganizationUserAssociationType | null>(null);

// --- Highlight active association ---
const isAssocActive = (
  assoc: OrganizationUserAssociationType | null
): boolean => {
  if (!assoc) return false;
  if (assoc.removedAt) return false;
  return (
    assoc.organization?.id === userStore.activeOrgAssociation?.organization?.id
  );
};

// --- Show inactive associations ---
const isAssocInactive = (
  assoc: OrganizationUserAssociationType | null
): boolean => {
  if (!assoc) return true;
  return !!assoc.removedAt;
};

const showInactiveAssociations = ref(false);

// --- Computed properties ---
// compute property to check whether user is owner and the only remaining active member
const isOwnerAndLastActiveMember = computed(() => {
  if (!props.associations) return false;
  const activeMembers = props.associations.filter(
    (assoc) => !isAssocInactive(assoc)
  );
  return (
    activeMembers.length === 1 &&
    activeMembers[0].user?.id === userStore.id &&
    props.isOwner
  );
});

const filteredAssociations = computed(() => {
  if (!props.associations) return [];

  let associations = props.associations;

  if (!showInactiveAssociations.value) {
    associations = associations.filter((key) => !isAssocInactive(key));
  }

  return associations.map((key) => ({
    ...key, // Spread original key properties
    _rowVariant: getRowVariant(key), // Assign row variant dynamically
  }));
});

const getRowVariant = (
  item: OrganizationUserAssociationType | null
): string => {
  if (!item) return '';
  // if (isAssocActive(item)) { return 'primary'; }
  if (isAssocInactive(item)) {
    return 'danger';
  }
  return ''; // previously 'success';
};

// --- Association table fields (dynamic)  ---
const assocTableFields = computed(() => {
  const fields = [
    { key: 'userRole', label: 'Role', sortable: true, tdClass: 'align-middle' },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      tdClass: 'align-middle',
    },
    {
      key: 'createdAt',
      label: 'From',
      sortable: true,
      formatter: formatDate,
      tdClass: 'align-middle',
    },
    {
      key: 'removedAt',
      label: 'To',
      sortable: true,
      formatter: (v: Date | string | undefined | null) => formatDate(v, '-'),
      tdClass: 'align-middle',
    },
    { key: 'id', label: '', tdClass: 'align-middle text-end min-width' },
  ];

  if (props.showEmail) {
    fields.splice(0, 0, {
      key: 'user.email',
      label: 'E-mail',
      sortable: true,
      tdClass: 'align-middle',
    });
    fields.splice(0, 0, {
      key: 'userName',
      label: 'Name',
      sortable: true,
      tdClass: 'align-middle',
    });
  }

  if (props.showOrganization) {
    fields.splice(0, 0, {
      key: 'organizationName',
      label: 'Organization',
      sortable: true,
      tdClass: 'align-middle',
    });
  }

  return fields;
});

// --- Initialize Organization Mutations ---
const showOrgOwnerUpdateSuccess = ref(false);
const showAssocUpdateSuccess = ref(false);
const showAssocRemoveSuccess = ref(false);

const loadingOrgOwnerUpdate = ref(false);
const loadingAssocUpdate = ref(false);
const loadingAssocRemove = ref(false);

const errorOrgOwnerUpdate = ref<string | null>(null);
const errorAssocUpdate = ref<string | null>(null);
const errorAssocRemove = ref<string | null>(null);

const {
  mutate: updateOrgOwnerMut,
  onDone: onUpdateOrgOwnerDone,
  onError: onUpdateOrgOwnerError,
} = useMutation(OrganizationOwnerUpdateDocument);
const {
  mutate: updateOrgAssociationMut,
  onDone: onUpdateOrgAssociationDone,
  onError: onUpdateOrgAssociationError,
} = useMutation(OrganizationUserAssociationUpdateDocument);
const {
  mutate: removeOrgAssociationMut,
  onDone: onRemoveOrgAssociationDone,
  onError: onRemoveOrgAssociationError,
} = useMutation(OrganizationUserAssociationRemoveDocument);

// --- Handle mutation responses ---
onUpdateOrgOwnerDone((res) => {
  loadingOrgOwnerUpdate.value = false;
  if (res.data && res.data.organizationOwnerUpdate) {
    console.debug(
      'Organization owner updated:',
      res.data.organizationOwnerUpdate
    );
    // emit event so the parent can refresh the memberships
    showOrgOwnerUpdateSuccess.value = true;
    emits('membershipsUpdated');
    // close the modal with delay
    setTimeout(() => {
      assocNewOwner.value = null;
      showUpdateOwnerModal.value = false;
      showOrgOwnerUpdateSuccess.value = true;
    }, 2000);
  } else {
    console.error('Organization owner update failed:', res.data);
    errorAssocRemove.value = `Organization owner update failed: ${res.data}`;
  }
});
onUpdateOrgOwnerError((err) => {
  console.error('Error updating organization owner:', err);
  loadingOrgOwnerUpdate.value = false;
  errorOrgOwnerUpdate.value = err.message;
});
onUpdateOrgAssociationDone((res) => {
  loadingAssocUpdate.value = false;
  if (res.data && res.data.organizationUserAssociationUpdate) {
    console.debug(
      'Organization-user association updated:',
      res.data.organizationUserAssociationUpdate
    );
    // emit event so the parent can refresh the memberships
    showAssocUpdateSuccess.value = true;
    emits('membershipsUpdated');
    // close the modal with delay
    setTimeout(() => {
      assocToEdit.value = null;
      showAssocEditModal.value = false;
      showAssocUpdateSuccess.value = false;
    }, 2000);
  } else {
    console.error('Organization-user association update failed:', res.data);
    errorAssocUpdate.value = `Organization-user association update failed: ${res.data}`;
  }
});
onUpdateOrgAssociationError((err) => {
  console.error('Error updating organization-user association:', err);
  loadingAssocUpdate.value = false;
  errorAssocUpdate.value = err.message;
});
onRemoveOrgAssociationDone((res) => {
  loadingAssocRemove.value = false;
  if (res.data && res.data.organizationUserAssociationRemove) {
    console.debug(
      'Organization-user association removed:',
      res.data.organizationUserAssociationRemove
    );
    // emit event so the parent can refresh the memberships
    showAssocRemoveSuccess.value = true;
    emits('membershipsUpdated');
    // close the modal with delay
    setTimeout(() => {
      assocToRemove.value = null;
      showRemoveModal.value = false;
      showAssocRemoveSuccess.value = false;
    }, 2000);
  } else {
    console.error('Organization-user association removal failed:', res.data);
    errorAssocRemove.value = `Organization-user association removal failed: ${res.data}`;
  }
});
onRemoveOrgAssociationError((err) => {
  console.error('Error removing organization-user association:', err);
  loadingAssocRemove.value = false;
  errorAssocRemove.value = err.message;
});

// -- Handle mutation requests ---
// --- handle MemberShipList emits ---
function handleUpdateAssociationRequest() {
  if (!assocToEdit.value) {
    console.error('No association to update');
    return;
  }

  if (!assocToEdit.value.id) {
    console.error('No association ID specified in the update request');
    return;
  }
  console.debug(
    'Update of organization-user association',
    assocToEdit.value.id,
    'requested'
  );
  errorAssocUpdate.value = null;
  loadingAssocUpdate.value = true;
  updateOrgAssociationMut({
    id: assocToEdit.value.id,
    userRole: assocToEdit.value.userRole,
    description: assocToEdit.value.description,
  });
}
function handleUpdateOrgOwnerRequest() {
  if (!assocNewOwner.value?.user?.id) {
    console.error(
      'No new owner specified in the organization ownership request'
    );
    return;
  }
  console.debug('New owner', assocNewOwner.value.user.id, 'requested');
  console.debug(
    'Update of organization, new owner',
    assocNewOwner.value.user.id,
    'requested'
  );
  errorOrgOwnerUpdate.value = null;
  loadingOrgOwnerUpdate.value = true;
  updateOrgOwnerMut({ newOwnerId: assocNewOwner.value.user.id });
}
function handleRemoveAssociationRequest() {
  if (!assocToRemove.value?.id) {
    console.error('No association ID specified in the removal request');
    return;
  }
  console.debug(
    'Remove organization-user association',
    assocToRemove.value.id,
    'requested'
  );
  errorAssocRemove.value = null;
  loadingAssocRemove.value = true;
  removeOrgAssociationMut({ id: assocToRemove.value.id });
}

// --- Handlers ---
function updateAssociationButtonClicked(
  assoc: OrganizationUserAssociationType
) {
  assocToEdit.value = assoc;
  showAssocEditModal.value = true;
}

function setAsOwnerButtonClicked(assoc: OrganizationUserAssociationType) {
  if (!assoc) {
    console.error('No association to update');
    return;
  }
  assocNewOwner.value = assoc;
  showUpdateOwnerModal.value = true;
}

function removeAssociationButtonClicked(
  assoc: OrganizationUserAssociationType
) {
  assocToRemove.value = assoc;
  showRemoveModal.value = true;
}

const getRowClass = (item: any, type: string) => {
  // Only apply logic to data rows and only if the prop is enabled
  if (!item || type !== 'row' || !props.showActiveStatus) return;

  // Use your existing helper to check if this specific row is the active one
  if (isAssocActive(item)) {
    return 'fw-bold'; // Bootstrap 5 class for bold. Use 'font-weight-bold' for BS4.
  }
};

function rowClicked(
  assoc: OrganizationUserAssociationType,
  index: number,
  event: Event
) {
  emits('rowClicked', assoc);
}

function switchOrg(assoc: OrganizationUserAssociationType) {
  if (!assoc || !assoc.organization) {
    console.error('No organization to switch to');
    return;
  }
  assocToActivate.value = assoc;
  showAssocActivateModal.value = true;
}

function handleOrgActivated(orgId: number) {
  console.debug('Organization activated with ID:', orgId);
  emits('membershipsUpdated');
  userStore.activeOrgAssociation = assocToActivate.value;

  // clear the modal state with delay
  setTimeout(() => {
    assocToActivate.value = null;
    showAssocActivateModal.value = false;
  }, 1500);
}
function handleOrgActivateClose() {
  assocToActivate.value = null;
  showAssocActivateModal.value = false;
}
</script>

<template>
  <div>
    <BCard>
      <b-table
        :items="filteredAssociations"
        :busy="props.isLoading"
        :fields="assocTableFields"
        small
        show-empty
        striped
        responsive
        hover
        class="browser-table"
        :tbody-tr-class="getRowClass"
        @row-clicked="rowClicked"
      >
        <!-- table busy -->
        <template #table-busy>
          <div
            class="d-flex align-items-center justify-content-center"
            style="min-height: 100px"
          >
            <b-spinner small class="align-middle me-1"></b-spinner>
            <span class="ms-1">Loading organization membership data...</span>
          </div>
        </template>

        <!-- table empty -->
        <template #empty>
          <div class="text-center my-5">
            <p class="text-muted">No data found.</p>
          </div>
        </template>

        <!-- table content -->
        <template #cell(organizationName)="data">
          {{ data.item.organization?.name }}
          <BBadge
            v-if="props.showActiveStatus && isAssocActive(data.item)"
            variant="primary"
            class="ms-1"
            title="This is the currently active organization"
            >A</BBadge
          >
        </template>
        <template #cell(userName)="data">
          <span
            >{{ data.item.user.givenName }}
            {{ data.item.user.familyName }}</span
          >
        </template>
        <template #cell(id)="data">
          <BButtonGroup>
            <b-button
              v-if="
                !props.showActiveStatus &&
                !isAssocInactive(data.item) &&
                data.item.userRole !== ORGANIZATION_USER_ROLES.OWNER &&
                props.canManage
              "
              variant="outline-warning"
              size="sm"
              @click="updateAssociationButtonClicked(data.item)"
            >
              <font-awesome-icon :icon="['fas', 'pencil']" /> Edit
            </b-button>
            <b-button
              v-if="
                !props.showActiveStatus &&
                props.isOwner &&
                !isAssocInactive(data.item) &&
                data.item.userRole !== ORGANIZATION_USER_ROLES.OWNER
              "
              variant="outline-secondary"
              size="sm"
              @click="setAsOwnerButtonClicked(data.item)"
            >
              <font-awesome-icon :icon="['fas', 'user']" /> Set Owner
            </b-button>
            <b-button
              v-if="
                !isAssocInactive(data.item) &&
                (data.item.userRole !== ORGANIZATION_USER_ROLES.OWNER ||
                  isOwnerAndLastActiveMember)
              "
              variant="outline-danger"
              size="sm"
              @click="removeAssociationButtonClicked(data.item)"
            >
              <font-awesome-icon
                :icon="['fas', 'person-walking-dashed-line-arrow-right']"
                class="me-1"
              />
              <span v-if="data.item.user.id === userStore.id">Leave</span>
              <span v-else>Remove</span>
            </b-button>
            <!-- Active Organization -->
            <b-button
              v-if="props.showActiveStatus && isAssocActive(data.item)"
              variant="primary"
              size="sm"
              @click="switchOrg(data.item)"
            >
              <font-awesome-icon :icon="['fas', 'check']" /> Active
            </b-button>
            <b-button
              v-else-if="props.showActiveStatus && !isAssocInactive(data.item)"
              variant="outline-primary"
              size="sm"
              @click="switchOrg(data.item)"
            >
              Activate
            </b-button>
          </BButtonGroup>
        </template>
      </b-table>
    </BCard>

    <b-form-checkbox
      v-model="showInactiveAssociations"
      name="check-button"
      switch
    >
      show inactive memberships
    </b-form-checkbox>

    <!-- Organization Activation Component -->
    <OrganizationActivate
      v-if="assocToActivate"
      :show="showAssocActivateModal"
      :association="assocToActivate"
      @orgActivated="handleOrgActivated"
      @close="handleOrgActivateClose"
    />

    <!-- Details Modal -->
    <b-modal v-if="assocToEdit" id="detailsModal" v-model="showAssocEditModal">
      <!-- Modal title -->
      <template #title>
        <font-awesome-icon :icon="['fas', 'pencil']" class="me-1" />
        Organization-user association details
      </template>

      <!-- Modal body -->
      <p>{{ assocToEdit.user?.email }}</p>
      <b-form-select
        id="assoc-edit-role"
        v-model="assocToEdit.userRole"
        :options="OrganizationMembershipModeSelectOptions"
        class="form-control mb-2"
      />
      <b-form-textarea
        v-model="assocToEdit.description"
        id="assoc-edit-description"
        label="Description"
        placeholder="Enter a description"
        type="text"
        class="mb-2"
      ></b-form-textarea>

      <!-- Error alert -->
      <b-alert v-model="errorAssocUpdate" variant="danger" class="mt-3">
        {{ errorAssocUpdate }}
      </b-alert>

      <!-- Modal footer with action buttons -->
      <template #footer>
        <b-button
          variant="secondary"
          @click="showAssocEditModal = false"
          class="me-2"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="handleUpdateAssociationRequest"
          :disabled="loadingAssocUpdate"
        >
          <span v-if="loadingAssocUpdate">
            <b-spinner small />
            Saving Changes
          </span>
          <span v-else-if="showAssocUpdateSuccess">
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
            Changes Saved
          </span>
          <span v-else>Save Changes</span>
        </b-button>
      </template>
    </b-modal>

    <!-- Update Owner Modal -->
    <b-modal
      v-if="assocNewOwner"
      id="confirmUpdateOwnerModal"
      v-model="showUpdateOwnerModal"
    >
      <!-- Modal title -->
      <template #title>
        <font-awesome-icon :icon="['fas', 'user-tie']" class="me-1" />
        Confirm ogranization ownership transfer
      </template>

      <!-- Modal body -->
      <p>
        Are you sure you want to set
        <strong>{{ assocNewOwner.user?.email }}</strong> as new organization
        owner? As a result of this action, you will lose the organization
        ownership. You will become organization administrator.
      </p>

      <!-- Error alert -->
      <b-alert v-model="errorOrgOwnerUpdate" variant="danger" class="mt-3">
        {{ errorOrgOwnerUpdate }}
      </b-alert>

      <!-- Modal footer with action buttons -->
      <template #footer>
        <b-button
          variant="secondary"
          @click="showUpdateOwnerModal = false"
          class="me-2"
        >
          Cancel
        </b-button>
        <b-button
          variant="danger"
          @click="handleUpdateOrgOwnerRequest"
          :disabled="loadingOrgOwnerUpdate"
        >
          <span v-if="loadingOrgOwnerUpdate">
            <b-spinner small />
            Saving Changes
          </span>
          <span v-else-if="showOrgOwnerUpdateSuccess">
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
            Changes Saved
          </span>
          <span v-else>Confirm</span>
        </b-button>
      </template>
    </b-modal>

    <!-- Remove Association Modal -->
    <b-modal
      v-if="assocToRemove"
      id="confirmRemoveModal"
      v-model="showRemoveModal"
    >
      <!-- Modal title -->
      <template #title>
        <font-awesome-icon
          :icon="['fas', 'person-walking-dashed-line-arrow-right']"
          class="me-1"
        />
        Confirm action
      </template>

      <!-- Modal body -->
      <div v-if="isOwnerAndLastActiveMember">
        <p>
          You are about to suspend the organization and give up the organization
          ownership. As a result of this action, this organization will become
          suspended indefinitely. No users will be able to join it.
        </p>
        <p>
          <strong>
            Considering adding a new user to the organization and transfer the
            ownership to them before proceeding.
          </strong>
        </p>
        <p>Are you sure you still want to proceed with this action?</p>
      </div>
      <p v-else-if="assocToRemove.user?.id === userStore.id">
        Are you sure you want to leave this organization?
      </p>
      <p v-else>
        Are you sure you want to remove this user from the organization?
      </p>

      <!-- Error alert -->
      <b-alert v-model="errorAssocRemove" variant="danger" class="mt-3">
        {{ errorAssocRemove }}
      </b-alert>

      <!-- Modal footer with action buttons -->
      <template #footer>
        <b-button
          variant="secondary"
          @click="showRemoveModal = false"
          class="me-2"
        >
          Cancel
        </b-button>
        <b-button
          variant="danger"
          @click="handleRemoveAssociationRequest"
          :disabled="loadingAssocRemove"
        >
          <span v-if="loadingAssocRemove">
            <b-spinner small />
            Saving Changes
          </span>
          <span v-else-if="showAssocRemoveSuccess">
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
            Changes Saved
          </span>
          <span v-else>Confirm</span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
